import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import './ChatBot.css';
import './ChatHistory.css';
import { chatBotService } from '../../core/services/chatBotService';
import { Message } from '../../Dto/chatbotStructuredResponse';
import StructuredResponseComponent from './StructuredResponse';
import ChatHistory from './ChatHistory';
import { useAppSelector } from '../../core/redux/hooks';
import { GetTenantId } from '../../core/services/sharedService';

const ChatBot: React.FC = () => {
    const isMobile = window.innerWidth < 768;
    const [isOpen, setIsOpen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [sessionStats, setSessionStats] = useState<any>(null);
    const [showChatHistory, setShowChatHistory] = useState(false);
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const isFirstMessageRef = useRef(false);

    // Get user info from Redux
    const authState = useAppSelector((state) => state.auth);
    const tenantId = GetTenantId();
    const userId = authState.users?.email || 'anonymous';

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    // When chatbot opens, scroll to bottom and focus input
    useEffect(() => {
        if (isOpen) {
            // Scroll to bottom when opening
            setTimeout(() => {
                scrollToBottom();
            }, 100);

            // Focus input box
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }, [isOpen, scrollToBottom]);

    // Only load session history when explicitly selecting from history (not during active chat)
    const loadSessionHistory = async (sessionIdToLoad: string) => {
        if (!sessionIdToLoad || !userId || !tenantId) return;
        
        try {
            const context = await chatBotService.getFullConversationContext(
                sessionIdToLoad,
                userId,
                tenantId
            );
            
            // Split each Q&A pair into separate user and bot messages for display
            const historyMessages: Message[] = [];
            context.forEach((msg: any, index: number) => {
                // Add user message (question only)
                historyMessages.push({
                    id: `history_q_${index}`,
                    question: msg.question,
                    rephrase_question: msg.rephrase_question,
                    is_on_topic: msg.is_on_topic,
                    created_at: msg.created_at ? new Date(msg.created_at) : undefined,
                    timestamp: msg.created_at ? new Date(msg.created_at) : new Date()
                });
                
                // Add bot message (answer) if it exists
                if (msg.answer) {
                    historyMessages.push({
                        id: `history_a_${index}`,
                        question: msg.question,
                        rephrase_question: msg.rephrase_question,
                        is_on_topic: msg.is_on_topic,
                        answer: msg.answer,
                        updated_at: msg.updated_at ? new Date(msg.updated_at) : undefined,
                        timestamp: msg.updated_at ? new Date(msg.updated_at) : new Date(),
                        execution_time: msg.execution_time
                    });
                }
            });
            
            setMessages(historyMessages);
            
            const stats = await chatBotService.getSessionStats(sessionIdToLoad, tenantId);
            setSessionStats(stats);
        } catch (error) {
            console.error('Error loading session:', error);
        }
    };

    const sanitizeInput = useCallback((input: string): string => {
        // Remove potentially dangerous characters and limit length
        return input
            .replace(/<script[^>]*>.*?<\/script>/gi, '')
            .replace(/<[^>]+>/g, '')
            .trim()
            .substring(0, 1000); // Limit to 1000 characters
    }, []);
    
    const handleSendMessage = useCallback(async () => {
        if (!inputText.trim() || isLoading) return;
        
        const sanitizedInput = sanitizeInput(inputText);
        if (!sanitizedInput) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            question: sanitizedInput,
            rephrase_question: sanitizedInput, // Will be updated by backend
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const query = sanitizedInput;
        setInputText('');
        setIsLoading(true);

        // Add placeholder bot message for streaming (same message object will be updated with answer)
        const botMessageId = (Date.now() + 1).toString();
        const botMessage: Message = {
            id: botMessageId,
            question: inputText.trim(),
            rephrase_question: inputText.trim(),
            answer: {
                content: '',
                tables: null,
                list: null,
                graph: null
            },
            timestamp: new Date(),
            isStreaming: true
        };
        setMessages(prev => [...prev, botMessage]);

        try {
            // Validate required data
            if (!userId || !tenantId) {
                console.error('[ERROR] Missing user or tenant information');
                setMessages(prev => 
                    prev.map(msg => 
                        msg.id === botMessageId 
                            ? { 
                                ...msg, 
                                answer: {
                                    content: '**Error**: User authentication required. Please log in again.',
                                    tables: null,
                                    list: null,
                                    graph: null
                                },
                                isStreaming: false 
                            }
                            : msg
                    )
                );
                setIsLoading(false);

                // Auto-focus input box after error
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                }, 100);
                return;
            }
            
            // Create session only when user sends first message
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                try {
                    const newSession = await chatBotService.createNewChatSession(userId, tenantId, 'New Chat');
                    currentSessionId = newSession.session_id;
                    setSessionId(currentSessionId);
                    isFirstMessageRef.current = true; // Mark this as the first message

                    // Immediately refresh chat history to show the new session (like ChatGPT)
                    setHistoryRefreshTrigger(prev => prev + 1);
                } catch (error) {
                    console.error('Failed to create session:', error);
                }
            }
            
            await chatBotService.sendStreamingQueryWithContext(
                query,
                currentSessionId || '',
                userId,
                tenantId,
                (chunk: string, isComplete: boolean, metadata?: any) => {
                    setMessages(prev =>
                        prev.map(msg => {
                            if (msg.id === botMessageId) {
                                // Handle metadata with structured components (during streaming or completion)
                                if (metadata?.structuredResponse) {
                                    return {
                                        ...msg,
                                        answer: {
                                            content: metadata.structuredResponse.content || msg.answer?.content || '',
                                            tables: metadata.structuredResponse.tables || msg.answer?.tables || null,
                                            list: metadata.structuredResponse.list || msg.answer?.list || null,
                                            graph: metadata.structuredResponse.graph || msg.answer?.graph || null
                                        },
                                        isStreaming: !isComplete,
                                        execution_time: metadata.execution_time
                                    };
                                }

                                // Handle streaming text content
                                if (chunk) {
                                    return {
                                        ...msg,
                                        answer: {
                                            content: (msg.answer?.content || '') + chunk,
                                            tables: msg.answer?.tables || null,
                                            list: msg.answer?.list || null,
                                            graph: msg.answer?.graph || null
                                        },
                                        isStreaming: !isComplete
                                    };
                                }

                                return msg;
                            }
                            return msg;
                        })
                    );

                    if (isComplete) {
                        setIsLoading(false);

                        // Auto-focus input box after streaming completes
                        setTimeout(() => {
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        }, 100);

                        // Update session stats
                        if (metadata?.conversation_context) {
                            setSessionStats((prev: any) => ({
                                ...prev,
                                message_count: (prev?.message_count || 0) + 1
                            }));
                        }

                        // Refresh chat history after first message is completed to get the updated title
                        if (isFirstMessageRef.current) {
                            isFirstMessageRef.current = false;
                            // Trigger chat history refresh after a short delay to ensure backend has updated the title
                            setTimeout(() => {
                                setHistoryRefreshTrigger(prev => prev + 1);
                            }, 500);
                        }
                    }
                },
                (error: string) => {
                    setMessages(prev => 
                        prev.map(msg => 
                            msg.id === botMessageId 
                                ? { 
                                    ...msg, 
                                    answer: {
                                        content: `**Error**: ${error}`,
                                        tables: null,
                                        list: null,
                                        graph: null
                                    },
                                    isStreaming: false 
                                }
                                : msg
                        )
                    );
                    setIsLoading(false);

                    // Auto-focus input box after error
                    setTimeout(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }, 100);
                }
            );
        } catch (error) {
            setMessages(prev => 
                prev.map(msg => 
                    msg.id === botMessageId 
                        ? { 
                            ...msg, 
                            answer: {
                                content: '**Error**: Sorry, there was an error processing your request. Please try again.',
                                tables: null,
                                list: null,
                                graph: null
                            },
                            isStreaming: false 
                        }
                        : msg
                )
            );
            setIsLoading(false);

            // Auto-focus input box after error
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [inputText, isLoading, sanitizeInput, userId, tenantId, sessionId]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSessionSelect = async (newSessionId: string) => {
        if (newSessionId === sessionId) return;
        
        // Close history sidebar immediately on mobile
        if (!isFullscreen) {
            setShowChatHistory(false);
        }
        
        setSessionId(newSessionId);
        setMessages([]);
        setIsLoading(true);
       
        // Focus input immediately
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 50);
        
        // Load session history
        await loadSessionHistory(newSessionId);
        setIsLoading(false);
    };

    const handleNewChat = () => {
        // Reset to fresh state - don't create session until user sends message
        setSessionId(null);
        setMessages([]);
        setSessionStats(null);
        isFirstMessageRef.current = false;

        // Close history sidebar on mobile
        if (!isFullscreen) {
            setShowChatHistory(false);
        }

        // Focus input immediately for new chat
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Show chat history when entering fullscreen, hide when exiting
    useEffect(() => {
        if (isFullscreen) {
            setShowChatHistory(true);
        } else {
            setShowChatHistory(false);
        }
    }, [isFullscreen]);

    // Check if user is admin to show chatbot
    const isAdmin = process.env.REACT_APP_ADMIN === localStorage.getItem("event");

    // Don't render chatbot if user is not admin
    if (!isAdmin) {
        return null;
    }

    return (
        <>
            {/* Floating Chat Button */}
            <div className="chatbot-float-button" onClick={() => setIsOpen(!isOpen)}>
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-chat-dots-fill'}`}></i>
            </div>

            {/* Chat Interface */}
            {isOpen && (
                <div className={`chatbot-container ${isFullscreen ? 'fullscreen' : ''} ${showChatHistory && isFullscreen ? 'with-sidebar' : ''}`}>
                    <div className="chatbot-card">
                        {/* Header - Full width in fullscreen */}
                        <div className="chatbot-header">
                            <div className="chatbot-header-content">
                                <div className="chatbot-agent-info">
                                    <div className="chatbot-avatar">
                                        <img src="/static/media/rcmlogo.c814099bdebc7ed06a15.png" alt="RCM" />
                                    </div>
                                    <div className="chatbot-agent-details">
                                        <div className="chatbot-agent-name">RCM Assistant</div>
                                        <div className="chatbot-agent-status">
                                            {sessionStats ? (
                                                <>
                                                    We are online! • {sessionStats.message_count} messages
                                                </>
                                            ) : (
                                                'We are online!'
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="chatbot-header-actions">
                                    <button 
                                        className="chatbot-history-btn"
                                        onClick={() => setShowChatHistory(!showChatHistory)}
                                        title="Chat History"
                                    >
                                        <i className="bi bi-clock-history"></i>
                                    </button>
                                    <button 
                                        className="chatbot-expand-btn"
                                        onClick={() => {
                                            setIsFullscreen(!isFullscreen);
                                            // Scroll to bottom after state update
                                            setTimeout(() => scrollToBottom(), 100);
                                        }}
                                        title={isFullscreen ? "Minimize" : "Expand"}
                                    >
                                        <i className={`bi ${isFullscreen ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'}`}></i>
                                    </button>
                                    <button 
                                        className="chatbot-close-btn"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Content Area - Split in fullscreen mode */}
                        {isFullscreen ? (
                            <div className="chatbot-content">
                                {/* Chat History Sidebar in fullscreen */}
                                {showChatHistory && (
                                    <div className="chat-history-overlay fullscreen">
                                        <ChatHistory
                                            isOpen={showChatHistory}
                                            onClose={() => setShowChatHistory(false)}
                                            userId={userId}
                                            tenantId={tenantId}
                                            currentSessionId={sessionId}
                                            onSessionSelect={handleSessionSelect}
                                            onNewChat={handleNewChat}
                                            isFullscreen={isFullscreen}
                                            refreshTrigger={historyRefreshTrigger}
                                        />
                                    </div>
                                )}
                                
                                {/* Messages Area */}
                                <div className="chatbot-messages-area">
                                    <div className="chatbot-messages">
                                        {messages.length === 0 && (
                                            <div className="chatbot-welcome">
                                                <div className="welcome-message">
                                                    <div className="welcome-text">Hi 👋 How can I help you?</div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {messages.map((message) => {
                                            const hasStructuredContent = message.answer && (
                                                message.answer.tables || 
                                                message.answer.list || 
                                                message.answer.graph
                                            );
                                            const isUserMessage = !message.answer || (
                                                message.answer.content === '' && 
                                                !message.isStreaming && 
                                                !hasStructuredContent
                                            );
                                            return (
                                                <div 
                                                    key={message.id} 
                                                    className={`chatbot-message ${isUserMessage ? 'user' : 'bot'}`}
                                                >
                                                    {isUserMessage ? (
                                                        <div className="message-bubble">
                                                            <div className="message-text">
                                                                {message.question}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="bot-message-content">
                                                            {message.answer?.content && (
                                                                <div className="message-bubble">
                                                                    <div className="message-text">
                                                                        <ReactMarkdown>{message.answer.content}</ReactMarkdown>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {message.answer && (
                                                                <StructuredResponseComponent
                                                                    response={message.answer}
                                                                    isMobile={isMobile}
                                                                    isExpanded={isFullscreen}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        
                                        {isLoading && (
                                            <div className="chatbot-message bot">
                                                <div className="message-bubble">
                                                    <div className="typing-indicator">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div ref={messagesEndRef} />
                                    </div>
                                    
                                    {/* Input Area */}
                                    <div className="chatbot-input">
                                        <div className="input-container">
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                placeholder="Enter your message..."
                                                value={inputText}
                                                onChange={(e) => setInputText(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                disabled={isLoading}
                                                className="message-input"
                                            />
                                            <button 
                                                className="send-button"
                                                onClick={handleSendMessage}
                                                disabled={!inputText.trim() || isLoading}
                                            >
                                                <i className="bi bi-send"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* Normal mode layout */
                            <>
                                <div className="chatbot-messages">
                                    {messages.length === 0 && (
                                        <div className="chatbot-welcome">
                                            <div className="welcome-message">
                                                <div className="welcome-text">Hi 👋 How can I help you?</div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {messages.map((message) => {
                                        const hasStructuredContent = message.answer && (
                                            message.answer.tables || 
                                            message.answer.list || 
                                            message.answer.graph
                                        );
                                        const isUserMessage = !message.answer || (
                                            message.answer.content === '' && 
                                            !message.isStreaming && 
                                            !hasStructuredContent
                                        );
                                        return (
                                            <div 
                                                key={message.id} 
                                                className={`chatbot-message ${isUserMessage ? 'user' : 'bot'}`}
                                            >
                                                {isUserMessage ? (
                                                    <div className="message-bubble">
                                                        <div className="message-text">
                                                            {message.question}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bot-message-content">
                                                        {message.answer?.content && (
                                                            <div className="message-bubble">
                                                                <div className="message-text">
                                                                    <ReactMarkdown>{message.answer.content}</ReactMarkdown>
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {message.answer && (
                                                            <StructuredResponseComponent
                                                                response={message.answer}
                                                                isMobile={window.innerWidth < 768}
                                                                isExpanded={isFullscreen}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    
                                    {isLoading && (
                                        <div className="chatbot-message bot">
                                            <div className="message-bubble">
                                                <div className="typing-indicator">
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div ref={messagesEndRef} />
                                </div>
                                
                                <div className="chatbot-input">
                                    <div className="input-container">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            placeholder="Enter your message..."
                                            value={inputText}
                                            onChange={(e) => setInputText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            disabled={isLoading}
                                            className="message-input"
                                        />
                                        <button 
                                            className="send-button"
                                            onClick={handleSendMessage}
                                            disabled={!inputText.trim() || isLoading}
                                        >
                                            <i className="bi bi-send"></i>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Chat History Sidebar - Show as overlay in non-fullscreen mode */}
                    {showChatHistory && !isFullscreen && (
                        <ChatHistory
                            isOpen={showChatHistory}
                            onClose={() => setShowChatHistory(false)}
                            userId={userId}
                            tenantId={tenantId}
                            currentSessionId={sessionId}
                            onSessionSelect={handleSessionSelect}
                            onNewChat={handleNewChat}
                            isFullscreen={isFullscreen}
                            refreshTrigger={historyRefreshTrigger}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default ChatBot;